/* eslint-disable camelcase */
import * as yup from "yup"
import React, { useCallback, useMemo, useRef, useState } from "react"
import moment from "moment"
import { Appearance, TextInput, TouchableOpacity, ViewStyle, Dimensions } from "react-native"
import { Box, Icon, View } from "native-base"

import { Cycle, Farm } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Formik } from "formik"
import { JalaIcon } from "../../jala-icon/jala-icon"
import { ModelField } from "../../field/model-field/model-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { PickerField } from "../../field/picker-field/picker-field"
import { Text } from "../../text/text"
import { color, nativeBaseTheme } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"

const windowWidth = Dimensions.get('window').width;

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4
}

const OTHERS: ViewStyle = {
  width: windowWidth,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: 16,
  // marginBottom: 14,
  // marginTop: 28
  paddingBottom: 14,
  marginTop: 14,
  paddingTop: 14,
  backgroundColor: color.palette.white
}

const ACTION_SHEET_ITEM: ViewStyle = {
  minHeight: 44,
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 16,
}

export interface WaterQualityMultiMeasurementFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number,
    pond_id?: number,
    cycle_id?: number,
    measured_date?: string,
    measured_time?: string,
    temperature?: number,
    ph?: number,
    salinity?: number,
    do?: number,
    transparency?: number,
    water_level?: number,
    do_percent?: number,
    turbidity?: number,
    ec?: number,
    color?: number,
    weather?: number,
    orp?: number
  }
}

/**
 * Describe your component here
 */
export const WaterQualityMultiMeasurementForm = observer(function WaterQualityMultiMeasurementForm(props: WaterQualityMultiMeasurementFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props

  const [ isHide, setHide ] = useState<boolean>(true)

  const waterColors = useMemo(() => [
    { value: 16711428, color: color.palette.yellowBox, name: translate('yellow') },
    { value: 14481705, color: color.palette.yellowGreenBox, name: translate('yellowGreen') },
    { value: 12612681, color: color.palette.brownBox, name: translate('brown') },
    { value: 8877606, color: color.palette.brownGreenBox, name: translate('brownGreen') },
    { value: 6899991, color: color.palette.darkBrownBox, name: translate('darkBrown') },
    { value: 12093004, color: color.palette.ligthBrownBox, name: translate('lightBrown') },
    { value: 9583392, color: color.palette.darkBrownRedBox, name: translate('brownRed') },
    { value: 5565239, color: color.palette.greeenBox, name: translate('green') },
    { value: 7964699, color: color.palette.greenBrownBox, name: translate('greenBrown') },
    { value: 3319427, color: color.palette.greenBlueBox, name: translate('greenBlue') },
    { value: 2644758, color: color.palette.darkerGreenBox, name: translate('darkGreenBrown') },
    { value: 2464018, color: color.palette.darkGreenBox, name: translate('darkGreen') },
    { value: 1842460, color: color.palette.black, name: translate('black') }
  ], [])

  const weathers = useMemo(() => [
    { value: 400, icon: 'weather-rain-storm', name: translate('thunderStorm') },
    { value: 450, icon: 'weather-rain-heavy', name: translate('rain') },
    { value: 500, icon: 'weather-rain-normal', name: translate('drizzle') },
    { value: 600, icon: 'weather-cloudy', name: translate('cloudy') },
    { value: 700, icon: 'weather-sunny-cloudy', name: translate('sunnyCloudy') },
    { value: 800, icon: 'weather-sunny', name: translate('sunny') },
    { value: 900, icon: 'weather-sunny-heavy', name: translate('scorching') }
  ], [])

  const input = {
    // farm_id: useRef<TextInput>(),
    // cycle_id: useRef<TextInput>(),
    // measured_date: useRef<TextInput>(),
    // measured_time: useRef<TextInput>(),
    ph: useRef<TextInput>(),
    salinity: useRef<TextInput>(),
    temperature: useRef<TextInput>(),
    do: useRef<TextInput>(),
    transparency: useRef<TextInput>(),
    color: useRef<TextInput>(),
    weather: useRef<TextInput>(),
    do_percent: useRef<TextInput>(),
    ec: useRef<TextInput>(),
    turbidity: useRef<TextInput>(),
    orp: useRef<TextInput>()
    // water_level: useRef<TextInput>(),
  }

  const nextInput = {
    // farm_id: () => input.cycle_id.current && input.cycle_id.current.focus(),
    // cycle_id: () => input.measured_date.current && input.measured_date.current.focus(),
    // measured_date: () => input.measured_time.current && input.measured_time.current.focus(),
    // measured_time: () => input.temperature.current && input.temperature.current.focus(),
    ph: () => input.salinity.current && input.salinity.current.focus(),
    salinity: () => input.temperature.current && input.temperature.current.focus(),
    temperature: () => input.do.current && input.do.current.focus(),
    do: () => input.transparency.current && input.transparency.current.focus(),
    transparency: () => input.orp.current && input.orp.current.focus(),
    orp: () => input.do_percent.current && input.do_percent.current.focus(),
    // color: () => input.weather.current && input.weather.current.focus(),
    // weather: () => input.do_percent.current && input.do_percent.current.focus(),
    do_percent: () => input.ec.current && input.ec.current.focus(),
    ec: () => input.turbidity.current && input.turbidity.current.focus(),
    // turbidity: () => input.water_level.current && input.water_level.current.focus(),
    // water_level: () => null,
  }

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("waterQualityMultiMeasurementForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("waterQualityMultiMeasurementForm.cycleId")),
    measured_date: yup.mixed().nullable().required().label(translate("waterQualityMultiMeasurementForm.measuredDate")),
    measured_time: yup.mixed().nullable().required().label(translate("waterQualityMultiMeasurementForm.measuredTime")),
    temperature: yup.number().min(0).max(100).nullable().label(translate("waterQualityMultiMeasurementForm.temperature")),
    ph: yup.number().min(0).max(14).nullable().label(translate("waterQualityMultiMeasurementForm.ph")),
    salinity: yup.number().min(0).max(42).nullable().label(translate("waterQualityMultiMeasurementForm.salinity")),
    do: yup.number().min(0).max(20).nullable().label(translate("waterQualityMultiMeasurementForm.do")),
    transparency: yup.number().min(0).max(1000).nullable().label(translate("waterQualityMultiMeasurementForm.transparency")),
    orp: yup.number().min(0).max(10000).nullable().label(translate("waterQualityMultiMeasurementForm.orp")),
    water_level: yup.number().min(0).max(1000).nullable().label(translate("waterQualityMultiMeasurementForm.waterLevel")),
    do_percent: yup.number().min(0).max(200).nullable().label(translate("waterQualityMultiMeasurementForm.doPercent")),
    turbidity: yup.number().min(0).nullable().label(translate("waterQualityMultiMeasurementForm.turbidity")),
    ec: yup.number().min(0).max(100000).nullable().label(translate("waterQualityMultiMeasurementForm.ec")),
    color: yup.number().nullable().label(translate("waterQualityMultiMeasurementForm.color")),
    weather: yup.number().nullable().label(translate("waterQualityMultiMeasurementForm.weather")),
  }).test(function (value) {
    const isValid = !![
      'temperature', 'ph', 'salinity', 'do', 'transparency', 'water_level', 'do_percent', 'turbidity', 'ec', 'color', 'weather',
    ].find((k) => value[k])
    if (isValid) return true;
    else return this.createError({
      path: 'temperature|ph|salinity|do|transparency|water_level|do_percent|turbidity|ec|color|weather',
      message: ''
    })
  })

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      measured_at: values.measured_date
        && values.measured_time
        && moment(`${values.measured_date} ${values.measured_time}`).format('YYYY-MM-DDTHH:mm:ssZ')
        || null,
    })
  }, [onSubmitProps])

  const initialValues = useMemo<WaterQualityMultiMeasurementFormProps["initialValues"]>(() => ({
    farm_id: null,
    pond_id: null,
    cycle_id: null,
    measured_date: moment().format('YYYY-MM-DD'),
    measured_time: moment().startOf('minutes').format('HH:mm:ss'),
    ...initialValuesProps
  }), [initialValuesProps]);

  const renderColorItem = useCallback(({ item }) => {
    if (!item) return null;
    return <View style={ACTION_SHEET_ITEM}>
      <Box backgroundColor={item.color} height="24px" width="24px" borderRadius="5px" mr="12px" />
      <Text text={item.name} preset={"body2"} colorScheme={colorScheme} isTruncated />
    </View>
  }, [colorScheme])

  const renderWeatherItem = useCallback(({ item }) => {
    if (!item) return null;
    return <View style={ACTION_SHEET_ITEM}>
      <Icon as={<JalaIcon name={item.icon}/>} color={color.primary} size="24px" mr="12px" />
      <Text text={item.name} preset={"body2"} colorScheme={colorScheme} isTruncated />
    </View>
  }, [colorScheme])

  const iconName = isHide ? "expand_more" : "expand_less"

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, initialValues, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="waterQualityMultiMeasurementForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue('cycle_id', null)
                await setFieldValue('pond_id', null)
                await setFieldValue('farm_id', value || null)
                await setFieldTouched('farm_id')
              }
            }}
          />
          <WalkthroughElement
            id="water-quality-form-first"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <View style={TITLE}>
              <Text tx="waterQualityMultiMeasurementForm.waterQualityMultiMeasurement" preset="title3" colorScheme={colorScheme} />
            </View>
            <View style={BORDER} />
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="waterQualityMultiMeasurementForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  model.fetch({
                    farm_id__eq: values.farm_id,
                    sort: 'started_at,desc|pond_name_alpha_numeric'
                  }).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                onValueChange: async (value, index, item) => {
                  await setFieldValue('pond_id', (item || {}).pond_id || null)
                  await setFieldValue('cycle_id', value || null)
                  await setFieldTouched('cycle_id')
                },
                titleTx: "waterQualityMultiMeasurementForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="measured_date"
              txPrefix="waterQualityMultiMeasurementForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            <View flexDirection="row">
              <DateTimePickerField
                name="measured_time"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                formControlProps={{ flex: 1, isRequired: true }}
                dateTimePickerProps={{ dateTimePickerProps: { mode: "time" } }}
              />
              <View
                flex={1}
                style={FORM_DROPDOWN}
              ></View>
            </View>
            <View flexDirection="row">
              <NumeralField
                name="ph"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ flex: 1 }} />
              <NumeralField
                name="salinity"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ flex: 1 }} />
            </View>
            <View flexDirection="row">
              <NumeralField
                name="temperature"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ flex: 1 }} />
              <NumeralField
                name="do"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ flex: 1 }} />
            </View>
            <View flexDirection="row">
              <NumeralField
                name="transparency"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ flex: 1 }} />
              <NumeralField
                name="orp"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ flex: 1 }} />
            </View>
            <View flexDirection="row">
              <PickerField
                name="color"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                formControlProps={{ flex: 1 }}
                pickerProps={{
                  data: waterColors,
                  dataKey: "value",
                  pickerActionsheetProps: { renderItem: renderColorItem },
                  renderSelectedItem: (item) => renderColorItem({ item }),
                  titleTx: "waterQualityMultiMeasurementForm.colorPlaceholder"
                }}
              />
              <PickerField
                name="weather"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                formControlProps={{ flex: 1 }}
                pickerProps={{
                  data: weathers,
                  dataKey: "value",
                  pickerActionsheetProps: { renderItem: renderWeatherItem },
                  renderSelectedItem: (item) => renderWeatherItem({ item }),
                  titleTx: "waterQualityMultiMeasurementForm.weatherPlaceholder"
                }}
              />
            </View>
          </WalkthroughElement>
          <WalkthroughElement
            id="water-quality-form-second"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <View style={OTHERS}>
              <Text tx="waterQualityMultiMeasurementForm.otherParameters" preset="title3" color={color.palette.gray800} />
              <TouchableOpacity onPress={() => setHide(!isHide)}>
                <Icon {...nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme })}
                      as={<JalaIcon name={iconName} />} size="24px" />
              </TouchableOpacity>
            </View>
          </WalkthroughElement>
          {!isHide &&
            <>
              <NumeralField
                name="do_percent"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="ec"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="turbidity"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="water_level"
                txPrefix="waterQualityMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
            </>
          }
        </>
      )}
    </Formik>
  )
})

WaterQualityMultiMeasurementForm.displayName = 'WaterQualityMultiMeasurementForm'
